import React from 'react';
import { Box, SxProps, Theme, useTheme } from '@mui/material';

/**
 * Mobile preview simulates a real mobile browser (e.g. iPhone 17 Pro Max).
 * Uses exact viewport dimensions + minimal baseline CSS. Content's own responsive
 * rules (@media queries) handle layout—if it works on real device, it works here.
 * Scales to fit the container so the full phone is visible without page scroll.
 */
/** iPhone 17 Pro Max CSS viewport (logical pixels). */
export const IPHONE_17_PRO_MAX = {
  viewportWidth: 440,
  viewportHeight: 956,
  /** Bezel width on each side (px). */
  bezel: 14,
} as const;

/** Max scale so the phone never looks oversized on large screens (more like a real iPhone). */
const MAX_PHONE_SCALE = 0.7;

/**
 * Minimal "mobile browser simulation" CSS.
 * Mirrors what real mobile Safari does: baseline styles only, no aggressive overrides.
 * Content's own @media (max-width: 600px) and responsive rules handle layout.
 * If it renders correctly on real iPhone, it will render correctly here.
 *
 * Footer and User Feedback sections use fixed widths (e.g. 536px) that overflow
 * on mobile viewport (440px). Targeted overrides fix these known sections.
 */
const MOBILE_BROWSER_CSS = `
  html { -webkit-text-size-adjust: 100%; }
  body { margin: 0; padding: 0; padding-top: 44px; overflow-x: hidden; width: 100%; min-height: 100vh; }
  /* Do not set font-family on body: email inline fonts (Arial, Poppins, etc.) must win for same line breaks as real client */
  img { max-width: 100%; height: auto; }

  /* User Feedback (FEEDBACK_V4, FEEDBACK_V3, FEEDBACK): fix 536px fixed width, tighten spacing */
  tr[id$="FEEDBACK_V4"] div,
  tr[id$="FEEDBACK_V4"] table,
  tr[id$="FEEDBACK_V3"] div,
  tr[id$="FEEDBACK_V3"] table,
  tr[id$="FEEDBACK"] div,
  tr[id$="FEEDBACK"] table {
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  tr[id$="FEEDBACK_V4"] tr,
  tr[id$="FEEDBACK_V3"] tr,
  tr[id$="FEEDBACK"] tr {
    display: table-row !important;
  }
  tr[id$="FEEDBACK_V4"] td,
  tr[id$="FEEDBACK_V3"] td,
  tr[id$="FEEDBACK"] td {
    display: table-cell !important;
    vertical-align: middle !important;
    box-sizing: border-box !important;
    padding: 12px 16px 0 !important;
  }
  tr[id$="FEEDBACK_V4"] td:first-of-type,
  tr[id$="FEEDBACK_V3"] td:first-of-type,
  tr[id$="FEEDBACK"] td:first-of-type {
    white-space: normal !important;
    width: auto !important;
    min-width: 0 !important;
  }
  tr[id$="FEEDBACK_V4"] td:not(:first-of-type),
  tr[id$="FEEDBACK_V3"] td:not(:first-of-type),
  tr[id$="FEEDBACK"] td:not(:first-of-type) {
    white-space: nowrap !important;
    width: 110px !important;
    max-width: 110px !important;
  }
  tr[id$="FEEDBACK_V4"] div,
  tr[id$="FEEDBACK_V3"] div,
  tr[id$="FEEDBACK"] div {
    padding-left: 16px !important;
    padding-bottom: 12px !important;
  }
  /* Thumbs icons: prevent clipping, ensure full display */
  tr[id$="FEEDBACK_V4"] td:not(:first-of-type) img,
  tr[id$="FEEDBACK_V3"] td:not(:first-of-type) img,
  tr[id$="FEEDBACK"] td:not(:first-of-type) img {
    display: inline-block !important;
    vertical-align: middle !important;
    object-fit: contain !important;
    width: 24px !important;
    height: 24px !important;
    max-width: 24px !important;
    max-height: 24px !important;
  }
  tr[id$="FEEDBACK_V4"] td:not(:first-of-type),
  tr[id$="FEEDBACK_V3"] td:not(:first-of-type),
  tr[id$="FEEDBACK"] td:not(:first-of-type) {
    overflow: visible !important;
    min-height: 46px !important;
  }

  /* Footer (FOOTER, FOOTER_V3, FOOTER_V4, NEW_FOOTER): prevent overflow, reduce extra space */
  tr[id$="FOOTER"] table,
  tr[id$="FOOTER_V3"] table,
  tr[id$="FOOTER_V4"] table,
  tr[id$="NEW_FOOTER"] table {
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
  tr[id$="FOOTER"] > td,
  tr[id$="FOOTER_V3"] > td,
  tr[id$="FOOTER_V4"] > td,
  tr[id$="NEW_FOOTER"] > td {
    max-width: 100% !important;
    box-sizing: border-box !important;
    padding: 20px 16px 16px !important;
    height: auto !important;
  }
  tr[id$="FOOTER"] .content-block-footer,
  tr[id$="FOOTER_V3"] .content-block-footer,
  tr[id$="FOOTER_V4"] .content-block-footer,
  tr[id$="NEW_FOOTER"] .content-block-footer {
    padding-top: 20px !important;
    padding-bottom: 16px !important;
  }
  tr[id$="FOOTER"] .table-not-adapt,
  tr[id$="FOOTER"] table[class*="social"],
  tr[id$="FOOTER_V3"] .table-not-adapt,
  tr[id$="FOOTER_V3"] table[class*="social"],
  tr[id$="FOOTER_V4"] .table-not-adapt,
  tr[id$="FOOTER_V4"] table[class*="social"],
  tr[id$="NEW_FOOTER"] .table-not-adapt,
  tr[id$="NEW_FOOTER"] table[class*="social"] {
    margin: 16px 0 !important;
  }

  /* Bill Summary (MONTHLY_SUMMARY_V2, MONTHLY_SUMMARY): align arrow with main amount */
  tr[id$="MONTHLY_SUMMARY_V2"] table tr td,
  tr[id$="MONTHLY_SUMMARY"] table tr td {
    vertical-align: bottom !important;
  }
  /* Bill Summary: keep amount column (and green row) on one line — target inner table where amounts live */
  tr[id$="MONTHLY_SUMMARY_V2"] table tr td:last-child,
  tr[id$="MONTHLY_SUMMARY"] table tr td:last-child,
  tr[id$="MONTHLY_SUMMARY_V2"] table table tr td:last-child,
  tr[id$="MONTHLY_SUMMARY"] table table tr td:last-child {
    white-space: nowrap !important;
  }
  /* All section tables: prevent numbers from breaking mid-digit (Bill Summary, Itemization, etc.) */
  tr[id] table td,
  tr[id] table th {
    word-break: normal !important;
    overflow-wrap: normal !important;
  }
  tr[id$="MONTHLY_SUMMARY_V2"] img[src*="Up_down"],
  tr[id$="MONTHLY_SUMMARY_V2"] img[alt*="arrow"],
  tr[id$="MONTHLY_SUMMARY_V2"] img[alt*="Up"],
  tr[id$="MONTHLY_SUMMARY_V2"] img[alt*="Down"],
  tr[id$="MONTHLY_SUMMARY"] img[src*="Up_down"],
  tr[id$="MONTHLY_SUMMARY"] img[alt*="arrow"],
  tr[id$="MONTHLY_SUMMARY"] img[alt*="Up"],
  tr[id$="MONTHLY_SUMMARY"] img[alt*="Down"] {
    vertical-align: middle !important;
    display: inline-block !important;
  }

  /* All section rows: ensure nested tables/divs respect viewport (HEADER, GREETING, MONTHLY_SUMMARY, ITEMIZATION, etc.) */
  tr[id] table,
  tr[id] div {
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
`;

/**
 * Builds a full HTML document string for rendering email content in an iframe
 * with a mobile viewport and email-client-style CSS. If the input is a full
 * document (html/body), extracts body content to avoid nested documents.
 */
export function buildEmailMobileDocument(html: string, viewportWidth: number): string {
  if (!html || !html.trim()) {
    const meta = `<meta charset="UTF-8"><meta name="viewport" content="width=${viewportWidth}, initial-scale=1, maximum-scale=1, user-scalable=no"><meta name="format-detection" content="telephone=no"><meta name="apple-mobile-web-app-capable" content="yes">`;
    return `<!DOCTYPE html><html><head>${meta}</head><body></body></html>`;
  }
  const trimmed = html.trim();
  const looksLikeFullDocument =
    trimmed.toLowerCase().startsWith('<!doctype') ||
    trimmed.toLowerCase().startsWith('<html');
  let bodyContent: string;
  let headStyles = '';
  if (looksLikeFullDocument) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const body = doc.body;
      bodyContent = body ? body.innerHTML : doc.documentElement?.innerHTML ?? html;
      // Preserve original head styles (includes @media queries for mobile responsiveness)
      const styleEls = doc.head?.querySelectorAll('style') ?? [];
      headStyles = Array.from(styleEls)
        .map((s) => s.textContent || '')
        .filter(Boolean)
        .join('\n');
    } catch {
      bodyContent = html;
    }
  } else {
    bodyContent = html;
    // Backend may return body-only or partial HTML (e.g. when sectionNames filter). Inject fallback
    // responsive styles so content still adapts to mobile viewport.
    if (!headStyles) {
      headStyles = `
        .wrapper, .webkit, .outer, center[class="wrapper"], div[class="webkit"], table[class="outer"] {
          max-width: 100% !important; width: 100% !important;
        }
        table { max-width: 100% !important; }
        img { max-width: 100% !important; height: auto !important; }
      `;
    }
  }
  const viewportMeta = `<meta charset="UTF-8"><meta name="viewport" content="width=${viewportWidth}, initial-scale=1, maximum-scale=1, user-scalable=no"><meta name="format-detection" content="telephone=no"><meta name="apple-mobile-web-app-capable" content="yes">`;
  const ourStyleTag = `<style>${MOBILE_BROWSER_CSS}</style>`;
  const originalStylesTag = headStyles ? `<style>${headStyles}</style>` : '';
  return `<!DOCTYPE html><html><head>${viewportMeta}${ourStyleTag}${originalStylesTag}</head><body>${bodyContent}</body></html>`;
}

export type MobilePreviewProps = {
  /** When set: render as Email mobile view (iframe with viewport + CSS). */
  html?: string;
  /** When set (and no html): render as Web widget mobile view; children go inside the phone frame. */
  children?: React.ReactNode;
  /** Layout width in CSS px. For HTML: iframe document viewport. For widget: content area width. Default: iPhone 17 Pro Max (440). */
  viewportWidth?: number;
  /** Layout height in CSS px. Default: iPhone 17 Pro Max (956). */
  viewportHeight?: number;
  /** If true, wrap content in the black bezel phone frame. */
  showPhoneFrame?: boolean;
  /** Extra styles for the root container. */
  sx?: SxProps<Theme>;
};

export const MobilePreview: React.FC<MobilePreviewProps> = ({
  html,
  children,
  viewportWidth = IPHONE_17_PRO_MAX.viewportWidth,
  viewportHeight = IPHONE_17_PRO_MAX.viewportHeight,
  showPhoneFrame = true,
  sx,
}) => {
  const theme = useTheme();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState(1);

  const srcdoc = React.useMemo(() => {
    if (html != null && html !== '') {
      return buildEmailMobileDocument(html, viewportWidth);
    }
    return undefined;
  }, [html, viewportWidth]);

  const bezel = IPHONE_17_PRO_MAX.bezel;
  const frameWidth = viewportWidth + bezel * 2;
  const frameHeight = viewportHeight + bezel * 2;

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const updateScale = () => {
      const { clientWidth, clientHeight } = el;
      if (clientWidth <= 0 || clientHeight <= 0) return;
      const scaleX = clientWidth / frameWidth;
      const scaleY = clientHeight / frameHeight;
      const nextScale = Math.min(MAX_PHONE_SCALE, scaleX, scaleY);
      setScale(nextScale);
    };
    updateScale();
    const ro = new ResizeObserver(updateScale);
    ro.observe(el);
    return () => ro.disconnect();
  }, [frameWidth, frameHeight]);

  const phoneFrameSx: SxProps<Theme> = {
    margin: '0 auto',
    width: `${frameWidth}px`,
    height: `${frameHeight}px`,
    bgcolor: '#000000',
    border: `${bezel}px solid #000000`,
    borderRadius: '40px',
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 20px 25px rgba(0,0,0,0.6)'
        : '0 20px 25px rgba(0,0,0,0.4)',
    overflow: 'hidden',
  };

  const innerAreaSx: SxProps<Theme> = {
    width: `${viewportWidth}px`,
    minWidth: `${viewportWidth}px`,
    maxWidth: `${viewportWidth}px`,
    height: '100%',
    bgcolor: 'background.paper',
    overflowY: 'auto',
    overflowX: 'hidden',
  };

  const content =
    html != null && html !== '' ? (
      <iframe
        title="Mobile email preview"
        srcDoc={srcdoc}
        style={{
          width: `${viewportWidth}px`,
          minWidth: `${viewportWidth}px`,
          maxWidth: `${viewportWidth}px`,
          height: '100%',
          minHeight: '100%',
          border: 'none',
          display: 'block',
          verticalAlign: 'top',
        }}
      />
    ) : children != null ? (
      children
    ) : null;

  if (content == null) {
    return null;
  }

  const inner = <Box sx={innerAreaSx}>{content}</Box>;

  if (showPhoneFrame) {
    return (
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          overflow: 'hidden',
          ...sx,
        }}
      >
        <Box
          sx={{
            transform: `scale(${scale})`,
            transformOrigin: 'top center',
          }}
        >
          <Box sx={phoneFrameSx}>{inner}</Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: viewportWidth,
        maxWidth: '100%',
        overflow: 'auto',
        ...sx,
      }}
    >
      {inner}
    </Box>
  );
};
